import { cdasLegacyShape, withStagedItems } from '@/api2/clinicalBridge';
import { defineChartDocumentList } from '@/stores/chartDocumentList';
import { useChartCdaStagedItemsStore } from '@/stores/chartCdaStagedItems';

/**
 * The open chart's C-CDAs, for the screens that read them out of `getAllData`
 * — the CDA page's dropdown, the document itself and its side bar
 * (`store/modules/cda.js`, `allData.cdas`). pacmny-be built this list into
 * the blob; the port reads `GET /cda-documents?filter[patient_id]=`, and the
 * `login/getAllData` getter lays it over the blob. The rows' rules are
 * `cdasLegacyShape`'s; how and when the list is read is
 * `defineChartDocumentList`'s.
 */
const chartCdas = defineChartDocumentList('chartCdas', {
  path: '/cda-documents',
  shape: cdasLegacyShape,
  failedKey: 'cdasLoadFailed',
});

export const useChartCdasStore = chartCdas.useStore;

/**
 * `{cdas, cdasLoadFailed}` for the chart in view, once read; `{}` until then.
 *
 * `openCdaId` is the uid of the C-CDA open on the CDA page (`login/cda`
 * `cdaId`). Its staged items — what Save would commit — are read and laid over
 * its row for the side bar, when it has any to review: scanned (`staged`) and
 * not yet in the chart. A read that failed marks the row `extractionFailed`.
 */
export function chartCdasForGetAllData(patientId, openCdaId = null) {
  const overlay = chartCdas.forGetAllData(patientId);

  if (!Array.isArray(overlay.cdas) || typeof openCdaId !== 'string') return overlay;

  const index = overlay.cdas.findIndex((row) => row.id === openCdaId);
  const row = index < 0 ? null : overlay.cdas[index];

  if (!row || row.isSaved || row.status !== 'staged') return overlay;

  const staged = useChartCdaStagedItemsStore();

  staged.ensureFor(openCdaId);

  const entry = staged.entries[openCdaId];

  if (!entry) return overlay;

  const cdas = overlay.cdas.slice();

  cdas[index] = entry.failed
    ? { ...row, extractionFailed: true }
    : { ...withStagedItems(row, entry.items), extractionShown: !entry.stale };

  return { ...overlay, cdas };
}
